import React, {useCallback, useEffect, useState} from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

import {useStores} from '../../../Hooks';
import {accessItemTypeEnum, workspaceUsersEnum} from '../../../serverClientShared/sharedEnums';
import {GroupsRequester} from '../../Domain/Groups/GroupsRequester';

const formatAccessSummary = (payload, translate) => {
    if (!payload) return translate('noAccess') || 'No access';
    const allNodes = [...(payload.includeAll || []), ...(payload.individual || [])];
    if (allNodes.length === 0) return translate('noAccess') || 'No access';

    const counts = {
        projects: allNodes.filter((n) => n.type === accessItemTypeEnum.PROJECT).length,
        folders: allNodes.filter((n) => n.type === accessItemTypeEnum.FOLDER).length,
        items: allNodes.filter((n) => n.type === accessItemTypeEnum.ITEM).length,
    };

    const parts = [];
    if (counts.projects > 0) parts.push(`${counts.projects} ${translate(counts.projects === 1 ? 'project' : 'projects')}`);
    if (counts.folders > 0) parts.push(`${counts.folders} ${translate(counts.folders === 1 ? 'folder' : 'folders')}`);
    if (counts.items > 0) parts.push(`${counts.items} ${translate(counts.items === 1 ? 'item' : 'items')}`);
    return parts.length ? parts.join(', ') : (translate('noAccess') || 'No access');
};

const AdminAccessBadge = ({translate}) => (
    <span
        className="inline-flex items-center px-2 py-0.5 rounded-menu text-xs font-medium"
        style={{backgroundColor: 'color-mix(in srgb, var(--classicbutton-normal-color) 12%, transparent)', color: 'var(--classicbutton-normal-color)'}}
    >
        {translate('admin')}
    </span>
);

const tooltipClasses = {
    tooltip: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input',
};

const GroupAccessInfo = () => (
    <Tooltip
        title="Groups also grant access. Open the selector to see the full picture."
        placement="top"
        componentsProps={{tooltip: {className: tooltipClasses.tooltip}}}
    >
        <InfoOutlinedIcon style={{fontSize: 16}} className="text-main-light cursor-help" />
    </Tooltip>
);

const AccessSelector = ({permissions, accessPayload, onAccessChange}) => {
    const {modalStore, generalStore} = useStores();
    const isAdmin = permissions.administrationRole === workspaceUsersEnum.WORKSPACE_ADMIN;
    const hasGroups = permissions.groups?.length > 0;
    const [inheritedSummary, setInheritedSummary] = useState(null);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const groupIds = (permissions.groups || []).map(g => g.GroupId || g);
                const result = await GroupsRequester.getInheritedAccessSummary(generalStore.workspace, groupIds);
                setInheritedSummary(result);
            } catch {
                setInheritedSummary(null);
            }
        };
        fetchSummary();
    }, [permissions.groups, generalStore.workspace]);

    const openAccessModal = useCallback(() => {
        modalStore.isAccessModalOpen = {
            targetType: 'user',
            groups: permissions.groups || [],
            targets: [{
                TrainingAndFieldRole: permissions.trainingAndFieldRole || 'none',
                ContentCreationRole: permissions.contentCreationRole || 'none',
            }],
            accessSummary: inheritedSummary,
            initialValue: accessPayload || null,
            onSave: (payload) => {
                onAccessChange(payload);
            },
        };
    }, [permissions, modalStore, accessPayload, onAccessChange, inheritedSummary]);

    const formatInheritedSummary = (summary) => {
        if (!summary) return null;
        const parts = [];
        if (summary.projects > 0) parts.push(`${summary.projects} ${generalStore.translate(summary.projects === 1 ? 'project' : 'projects')}`);
        if (summary.folders > 0) parts.push(`${summary.folders} ${generalStore.translate(summary.folders === 1 ? 'folder' : 'folders')}`);
        if (summary.items > 0) parts.push(`${summary.items} ${generalStore.translate(summary.items === 1 ? 'item' : 'items')}`);
        return parts.length ? `Via groups: ${parts.join(', ')}` : null;
    };

    const directText = formatAccessSummary(accessPayload, generalStore.translate);
    const inheritedText = formatInheritedSummary(inheritedSummary);
    const displayText = isAdmin
        ? (generalStore.translate('allItems') || 'All Items')
        : directText !== (generalStore.translate('noAccess') || 'No access')
            ? directText
            : inheritedText || directText;

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
                <label className="text-sm text-main-normal">
                    <span className="font-medium">Direct Project Access</span>
                    <span className="text-main-disabled">  (optional)</span>
                </label>
                {hasGroups && !isAdmin && <GroupAccessInfo />}
            </div>
            <div
                className={`flex items-center justify-between p-2 rounded-menu border border-panels-normal bg-windowsandmenus-normal ${isAdmin ? 'cursor-default' : 'cursor-pointer hover:bg-panels-normal hover:border-classic-button-normal'}`}
                style={{minHeight: 40}}
                onClick={isAdmin ? undefined : openAccessModal}
            >
                <div className="flex items-center gap-2">
                    <span className={`text-sm ${isAdmin ? 'text-link-normal' : accessPayload ? 'text-main-normal' : 'text-main-disabled'}`}>
                        {displayText}
                    </span>
                    {isAdmin && <AdminAccessBadge translate={generalStore.translate} />}
                </div>
                {!isAdmin && <KeyboardArrowDownIcon style={{fontSize: 20}} className="text-main-light" />}
            </div>
        </div>
    );
};

export default AccessSelector;
